import { create } from 'zustand'
import {
  createAutoResponderRule,
  deleteAutoResponderRule,
  listAutoResponderRules,
  sortAutoResponderRules,
  testAutoResponderMessage,
  updateAutoResponderRule,
  type AutoResponderConflictWarning,
  type AutoResponderListResponse,
  type AutoResponderMatchOperator,
  type AutoResponderRule,
  type AutoResponderRuleInput,
  type AutoResponderTestMatch,
} from '../api/autoResponder'

export type AutoResponderQueryEnabledFilter = 'all' | 'enabled' | 'disabled'

interface AutoResponderQueryState {
  page: number
  perPage: number
  search: string
  enabled: AutoResponderQueryEnabledFilter
}

export type AutoResponderFieldErrors = Partial<Record<string, string>>

export interface AutoResponderRuleTriggerDraft {
  id?: number
  match_operator: AutoResponderMatchOperator
  trigger_text: string
}

export interface AutoResponderRuleDraft {
  id?: number
  name: string
  response_text: string
  enabled: boolean
  active_from: string | null
  active_until: string | null
  triggers: AutoResponderRuleTriggerDraft[]
  updated_at?: string | null
}

interface AutoResponderEditorState {
  open: boolean
  mode: 'create' | 'edit'
  draft: AutoResponderRuleDraft | null
  saving: boolean
  errors: AutoResponderFieldErrors
  warnings: AutoResponderConflictWarning[]
  errorMessage: string | null
}

interface AutoResponderSimulatorState {
  messageText: string
  loading: boolean
  normalisedText: string
  match: AutoResponderTestMatch | null
  warnings: AutoResponderConflictWarning[]
  error: string | null
}

interface AutoResponderState {
  stationId: number | null
  rules: AutoResponderRule[]
  pagination: AutoResponderListResponse['pagination']
  loading: boolean
  error: string | null
  query: AutoResponderQueryState
  editor: AutoResponderEditorState
  simulator: AutoResponderSimulatorState
  lastMutationWarnings: AutoResponderConflictWarning[]
  lastMutationMessage: string | null
  lastMutationSeverity: 'success' | 'error' | 'warning' | null
  setQuery: (updates: Partial<AutoResponderQueryState>) => void
  fetchRules: (stationId: number, overrides?: Partial<AutoResponderQueryState>) => Promise<void>
  refresh: () => Promise<void>
  openCreateEditor: () => void
  openEditEditor: (ruleId: number) => void
  closeEditor: () => void
  setDraftField: <K extends keyof AutoResponderRuleDraft>(key: K, value: AutoResponderRuleDraft[K]) => void
  addDraftTrigger: (trigger?: Partial<AutoResponderRuleTriggerDraft>) => void
  updateDraftTrigger: (
    index: number,
    updates: Partial<AutoResponderRuleTriggerDraft>,
  ) => void
  removeDraftTrigger: (index: number) => void
  saveEditor: () => Promise<{ success: boolean; warnings?: AutoResponderConflictWarning[]; message?: string; error?: string }>
  deleteRule: (ruleId: number) => Promise<boolean>
  toggleRuleEnabled: (ruleId: number, enabled: boolean) => Promise<boolean>
  reorderRules: (orderedIds: number[]) => Promise<boolean>
  resetMutationState: () => void
  setSimulatorMessage: (text: string) => void
  runSimulator: (options?: { useDraft?: boolean }) => Promise<void>
  reset: () => void
}

const INITIAL_QUERY: AutoResponderQueryState = {
  page: 1,
  perPage: 20,
  search: '',
  enabled: 'all',
}

const createEmptyDraft = (): AutoResponderRuleDraft => ({
  name: '',
  response_text: '',
  enabled: true,
  active_from: null,
  active_until: null,
  triggers: [
    {
      match_operator: 'contains',
      trigger_text: '',
    },
  ],
})

const toDraftDateValue = (iso: string | null): string | null => {
  if (!iso) {
    return null
  }
  const match = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/)
  if (!match) {
    return null
  }
  return `${match[1]}T${match[2]}`
}

const toPayloadDateValue = (value: string | null): string | null => {
  if (!value) {
    return null
  }
  const trimmed = value.trim()
  if (trimmed === '') {
    return null
  }
  if (trimmed.includes('T')) {
    const [datePart, timePart] = trimmed.split('T')
    if (timePart.length === 5) {
      return `${datePart} ${timePart}:00`
    }
    return `${datePart} ${timePart}`
  }
  return trimmed.replace('T', ' ')
}

const ruleToDraft = (rule: AutoResponderRule): AutoResponderRuleDraft => ({
  id: rule.id,
  name: rule.name,
  response_text: rule.response_text,
  enabled: rule.enabled,
  active_from: toDraftDateValue(rule.active_from),
  active_until: toDraftDateValue(rule.active_until),
  triggers: rule.triggers.map((trigger) => ({
    id: trigger.id > 0 ? trigger.id : undefined,
    match_operator: trigger.match_operator,
    trigger_text: trigger.trigger_text,
  })),
  updated_at: rule.updated_at,
})

const draftToPayload = (draft: AutoResponderRuleDraft): AutoResponderRuleInput => ({
  id: draft.id,
  name: draft.name,
  response_text: draft.response_text,
  enabled: draft.enabled,
  active_from: toPayloadDateValue(draft.active_from),
  active_until: toPayloadDateValue(draft.active_until),
  triggers: draft.triggers.map((trigger) => ({
    id: trigger.id,
    match_operator: trigger.match_operator,
    trigger_text: trigger.trigger_text.trim(),
  })),
  updated_at: draft.updated_at,
})

const parseApiError = (
  error: unknown,
): { message: string; fields?: AutoResponderFieldErrors } => {
  const defaultMessage = error instanceof Error ? error.message : 'Request failed'
  if (!error || typeof error !== 'object') {
    return { message: defaultMessage }
  }

  const data = (error as { data?: unknown }).data
  if (data && typeof data === 'object') {
    if ('fields' in data && data.fields && typeof data.fields === 'object') {
      const fieldMap: AutoResponderFieldErrors = {}
      Object.entries(data.fields as Record<string, unknown>).forEach(([key, value]) => {
        if (typeof value === 'string' && value.trim() !== '') {
          fieldMap[key] = value
        }
      })
      return {
        message:
          'message' in data && typeof (data as { message?: unknown }).message === 'string'
            ? String((data as { message?: unknown }).message)
            : defaultMessage,
        fields: fieldMap,
      }
    }
    if ('message' in data && typeof (data as { message?: unknown }).message === 'string') {
      return { message: String((data as { message?: unknown }).message) }
    }
  }

  return { message: defaultMessage }
}

const updateRuleInList = (rules: AutoResponderRule[], updated: AutoResponderRule): AutoResponderRule[] => {
  const index = rules.findIndex((rule) => rule.id === updated.id)
  if (index === -1) {
    return [...rules, updated].sort((a, b) => a.sort_order - b.sort_order)
  }
  const next = [...rules]
  next[index] = updated
  return next.sort((a, b) => a.sort_order - b.sort_order)
}

const removeRuleFromList = (rules: AutoResponderRule[], ruleId: number): AutoResponderRule[] =>
  rules.filter((rule) => rule.id !== ruleId)

const reorderRuleList = (rules: AutoResponderRule[], orderedIds: number[]): AutoResponderRule[] => {
  const orderMap = new Map<number, number>()
  orderedIds.forEach((id, idx) => {
    if (!orderMap.has(id)) {
      orderMap.set(id, idx)
    }
  })

  return [...rules].sort((a, b) => {
    const aIndex = orderMap.get(a.id)
    const bIndex = orderMap.get(b.id)
    if (aIndex === undefined && bIndex === undefined) {
      return a.sort_order - b.sort_order
    }
    if (aIndex === undefined) {
      return 1
    }
    if (bIndex === undefined) {
      return -1
    }
    return aIndex - bIndex
  })
}

const useAutoResponderStore = create<AutoResponderState>((set, get) => ({
  stationId: null,
  rules: [],
  pagination: {
    total: 0,
    per_page: INITIAL_QUERY.perPage,
    current_page: INITIAL_QUERY.page,
    total_pages: 0,
  },
  loading: false,
  error: null,
  query: { ...INITIAL_QUERY },
  editor: {
    open: false,
    mode: 'create',
    draft: null,
    saving: false,
    errors: {},
    warnings: [],
    errorMessage: null,
  },
  simulator: {
    messageText: '',
    loading: false,
    normalisedText: '',
    match: null,
    warnings: [],
    error: null,
  },
  lastMutationWarnings: [],
  lastMutationMessage: null,
  lastMutationSeverity: null,
  setQuery: (updates) =>
    set((state) => ({
      query: { ...state.query, ...updates },
    })),
  fetchRules: async (stationId, overrides) => {
    const currentQuery = get().query
    const nextQuery: AutoResponderQueryState = {
      ...currentQuery,
      ...overrides,
    }

    set({ loading: true, error: null, stationId, query: nextQuery })

    try {
      const response = await listAutoResponderRules({
        stationId,
        page: nextQuery.page,
        perPage: nextQuery.perPage,
        search: nextQuery.search,
        enabled: nextQuery.enabled,
      })
      set({
        rules: response.rules,
        pagination: response.pagination,
        loading: false,
        stationId,
        query: nextQuery,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load auto responder rules.'
      set({
        loading: false,
        error: message,
        rules: [],
        stationId,
        pagination: {
          total: 0,
          per_page: nextQuery.perPage,
          current_page: nextQuery.page,
          total_pages: 0,
        },
      })
    }
  },
  refresh: async () => {
    const { stationId, query } = get()
    if (!stationId) {
      return
    }
    await get().fetchRules(stationId, query)
  },
  openCreateEditor: () =>
    set(() => ({
      editor: {
        open: true,
        mode: 'create',
        draft: createEmptyDraft(),
        saving: false,
        errors: {},
        warnings: [],
        errorMessage: null,
      },
    })),
  openEditEditor: (ruleId) => {
    const { rules } = get()
    const rule = rules.find((item) => item.id === ruleId)
    if (!rule) {
      return
    }
    set({
      editor: {
        open: true,
        mode: 'edit',
        draft: ruleToDraft(rule),
        saving: false,
        errors: {},
        warnings: rule.conflict_warnings ?? [],
        errorMessage: null,
      },
    })
  },
  closeEditor: () =>
    set(() => ({
      editor: {
        open: false,
        mode: 'create',
        draft: null,
        saving: false,
        errors: {},
        warnings: [],
        errorMessage: null,
      },
    })),
  setDraftField: (key, value) =>
    set((state) => {
      if (!state.editor.draft) {
        return state
      }
      return {
        editor: {
          ...state.editor,
          draft: { ...state.editor.draft, [key]: value },
        },
      }
    }),
  addDraftTrigger: (trigger) =>
    set((state) => {
      if (!state.editor.draft) {
        return state
      }
      const nextTrigger: AutoResponderRuleTriggerDraft = {
        match_operator: trigger?.match_operator ?? 'contains',
        trigger_text: trigger?.trigger_text ?? '',
      }
      return {
        editor: {
          ...state.editor,
          draft: {
            ...state.editor.draft,
            triggers: [...state.editor.draft.triggers, nextTrigger],
          },
        },
      }
    }),
  updateDraftTrigger: (index, updates) =>
    set((state) => {
      if (!state.editor.draft) {
        return state
      }
      const triggers = state.editor.draft.triggers
      if (index < 0 || index >= triggers.length) {
        return state
      }
      const next = triggers.map((trigger, idx) =>
        idx === index
          ? {
              ...trigger,
              ...updates,
            }
          : trigger,
      )
      return {
        editor: {
          ...state.editor,
          draft: {
            ...state.editor.draft,
            triggers: next,
          },
        },
      }
    }),
  removeDraftTrigger: (index) =>
    set((state) => {
      if (!state.editor.draft) {
        return state
      }
      const triggers = state.editor.draft.triggers
      if (index < 0 || index >= triggers.length) {
        return state
      }
      const next = triggers.filter((_, idx) => idx !== index)
      return {
        editor: {
          ...state.editor,
          draft: {
            ...state.editor.draft,
            triggers: next.length > 0 ? next : triggers,
          },
        },
      }
    }),
  saveEditor: async () => {
    const { stationId, editor, refresh } = get()
    if (!stationId || !editor.draft) {
      return { success: false, error: 'Station not selected.' }
    }
    set((state) => ({
      editor: { ...state.editor, saving: true, errors: {}, errorMessage: null },
    }))
    try {
      const payload = draftToPayload(editor.draft)
      const response =
        editor.mode === 'edit' && payload.id
          ? await updateAutoResponderRule(stationId, payload.id, payload)
          : await createAutoResponderRule(stationId, payload)

      const updatedRule = response.rule
      if (updatedRule) {
        set((state) => ({
          rules: updateRuleInList(state.rules, updatedRule),
        }))
      }

      await refresh()

      const successMessage = editor.mode === 'edit' ? 'Rule updated.' : 'Rule created.'
      set({
        editor: {
          open: false,
          mode: 'create',
          draft: null,
          saving: false,
          errors: {},
          warnings: response.warnings ?? [],
          errorMessage: null,
        },
        lastMutationWarnings: response.warnings ?? [],
        lastMutationMessage: successMessage,
        lastMutationSeverity: 'success',
      })
      return { success: true, warnings: response.warnings ?? [], message: successMessage }
    } catch (error) {
      const parsed = parseApiError(error)
      set((state) => ({
        editor: {
          ...state.editor,
          saving: false,
          errors: parsed.fields ?? {},
          errorMessage: parsed.message,
        },
      }))
      return { success: false, error: parsed.message }
    }
  },
  deleteRule: async (ruleId) => {
    const { stationId, refresh } = get()
    if (!stationId) {
      return false
    }
    try {
      await deleteAutoResponderRule(stationId, ruleId)
      set((state) => ({
        rules: removeRuleFromList(state.rules, ruleId),
        lastMutationMessage: 'Rule deleted.',
        lastMutationWarnings: [],
        lastMutationSeverity: 'success',
      }))
      await refresh()
      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to delete rule.'
      set({ lastMutationMessage: message, lastMutationWarnings: [], lastMutationSeverity: 'error' })
      return false
    }
  },
  toggleRuleEnabled: async (ruleId, enabled) => {
    const { stationId, rules, refresh } = get()
    if (!stationId) {
      return false
    }
    const rule = rules.find((item) => item.id === ruleId)
    if (!rule) {
      return false
    }
    const draft = ruleToDraft({ ...rule, enabled })
    try {
      const response = await updateAutoResponderRule(stationId, ruleId, draftToPayload(draft))
      if (response.rule) {
        set((state) => ({
          rules: updateRuleInList(state.rules, response.rule ?? rule),
        }))
      }
      await refresh()
      set({
        lastMutationWarnings: response.warnings ?? [],
        lastMutationMessage: enabled ? 'Rule enabled.' : 'Rule disabled.',
        lastMutationSeverity: 'success',
      })
      return true
    } catch (error) {
      const parsed = parseApiError(error)
      set({ lastMutationWarnings: [], lastMutationMessage: parsed.message, lastMutationSeverity: 'error' })
      return false
    }
  },
  reorderRules: async (orderedIds) => {
    const { stationId } = get()
    if (!stationId) {
      return false
    }
    try {
      await sortAutoResponderRules(stationId, orderedIds)
      set((state) => ({
        rules: reorderRuleList(state.rules, orderedIds),
        lastMutationMessage: 'Rule order updated.',
        lastMutationWarnings: [],
        lastMutationSeverity: 'success',
      }))
      await get().refresh()
      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to reorder rules.'
      set({ lastMutationMessage: message, lastMutationWarnings: [], lastMutationSeverity: 'error' })
      return false
    }
  },
  resetMutationState: () => set({ lastMutationWarnings: [], lastMutationMessage: null, lastMutationSeverity: null }),
  setSimulatorMessage: (text) =>
    set((state) => ({
      simulator: { ...state.simulator, messageText: text },
    })),
  runSimulator: async (options) => {
    const { stationId, simulator, editor } = get()
    if (!stationId) {
      set({
        simulator: {
          ...simulator,
          error: 'Station not selected.',
        },
      })
      return
    }
    set({
      simulator: { ...simulator, loading: true, error: null, warnings: [] },
    })
    try {
      const payload: AutoResponderRuleInput | undefined =
        options?.useDraft && editor.draft ? draftToPayload(editor.draft) : undefined
      const response = await testAutoResponderMessage(stationId, {
        message_text: simulator.messageText,
        draft_rule: payload,
      })
      set({
        simulator: {
          messageText: simulator.messageText,
          loading: false,
          normalisedText: response.normalised_text,
          match: response.match,
          warnings: response.warnings ?? [],
          error: null,
        },
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to run simulator.'
      set({
        simulator: {
          ...simulator,
          loading: false,
          error: message,
        },
      })
    }
  },
  reset: () =>
    set({
      stationId: null,
      rules: [],
      pagination: {
        total: 0,
        per_page: INITIAL_QUERY.perPage,
        current_page: INITIAL_QUERY.page,
        total_pages: 0,
      },
      loading: false,
      error: null,
      query: { ...INITIAL_QUERY },
      editor: {
        open: false,
        mode: 'create',
        draft: null,
        saving: false,
        errors: {},
        warnings: [],
        errorMessage: null,
      },
      simulator: {
        messageText: '',
        loading: false,
        normalisedText: '',
        match: null,
        warnings: [],
        error: null,
      },
      lastMutationWarnings: [],
      lastMutationMessage: null,
      lastMutationSeverity: null,
    }),
}))

export default useAutoResponderStore
